import { ChangeDetectionStrategy, Component, OnDestroy } from "@angular/core";
import { FormArray, FormGroup, UntypedFormControl } from "@angular/forms";

import { RecordStateEnum, RulesetResourceTypeEnum, RouteEnum } from "src/app/shared/enums";
import {
  IConditionGroups,
  IExpectation,
  IExpectationItemForm,
  IRuleset,
} from "src/app/shared/interfaces";
import { CustomValidators } from "src/app/shared/validators";

import { TextConstants } from "@shared/constants";
import { ConfirmDialogSubmitButtonMode } from "@shared/types";
import { FormUtils } from "@shared/utils";

import { RulesetBaseClass } from "../ruleset-base.class";

@Component({
  standalone: false,
  selector: "app-edit-ruleset",
  templateUrl: "./edit-ruleset.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRulesetComponent extends RulesetBaseClass implements OnDestroy {
  public readonly translations: any = {
    titleAdd: $localize`:@@action.addruleset:Add ruleset`,
    titleEdit: $localize`:@@action.editruleset:Edit ruleset`,
    titleBackText: $localize`:@@shared.backtorulesets:Back to Rulesets`,
  };

  constructor() {
    super();
    const currentUrl = this.router.routerState.snapshot.url;
    const currentUrlResourceType = currentUrl.split("/")[4].toUpperCase();

    this.resourceType =
      currentUrlResourceType === "ORGANISATION"
        ? RulesetResourceTypeEnum.CONNECTION
        : (currentUrlResourceType as RulesetResourceTypeEnum);
    this.propertyOptions = this.rulesetsBuilderService.getPropertyOptions(this.resourceType);
  }

  public async loadData(rulesetId?: string): Promise<boolean> {
    this.isLoading.set(true);
    try {
      if (rulesetId) {
        this.element.set(await this.rulesetsService.get(rulesetId));
        this.isEditing.set(true);
        if (this.element().recordState === RecordStateEnum.ARCHIVED) {
          this.notificationService.showError(TextConstants.EDIT_ARCHIVED_RECORD_ERROR);
          await this.router.navigate([`/${RouteEnum.ADMIN_RULESETS_DETAILS}/${this.element().id}`]);

          return true;
        }
      }

      this.setupForm();

      await Promise.all([
        this.setLocationTypeOptions(),
        this.setLocationOptions(),
        this.setDocumentTypeOptions(),
      ]);

      this.isLoading.set(false);

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    }
  }

  public async onSave(isSaveOnly: boolean = true) {
    if (await FormUtils.isInvalid(this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return;
    }

    this.isLoading.set(true);

    try {
      const payload = await this.getPayload();

      if (!payload) {
        return;
      }

      const ruleset = await this.rulesetsService.createOrUpdate(payload, this.element()?.id);

      this.notificationService.showSuccess(
        this.isEditing()
          ? $localize`:@@shared.rulesetmodified:Ruleset modified`
          : $localize`:@@shared.rulesetcreated:Ruleset created`,
      );
      this.hasFormValuesChanged.set(false);
      if (isSaveOnly) {
        if (this.isEditing()) {
          await this.loadData(ruleset.id);
        } else {
          await this.navigateToEditRuleset(this.resourceType, ruleset.id, true);
        }
      }
    } catch (error) {
      this.notificationService.showError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  override requiresConfirmation(): boolean {
    return this.hasFormValuesChanged();
  }

  override async saveBeforeClosing(): Promise<void> {
    return this.onSave(false);
  }

  isSubmitButtonDisabled = (): boolean => {
    if (!this.isEditing()) {
      return false;
    }

    return !this.formGroup || this.formGroup.invalid || this.formGroup.pending;
  };

  protected override submitButtonMode(): ConfirmDialogSubmitButtonMode {
    return this.isSubmitButtonDisabled() ? "disabled" : undefined;
  }

  private buildNameControl(): UntypedFormControl {
    const entityExistsValidatorArgs: any = {
      resourceType: RulesetResourceTypeEnum.LOCATION,
    };

    return new UntypedFormControl(this.element()?.name ?? "", CustomValidators.required, [
      CustomValidators.entityAlreadyExists(
        this.rulesetsService,
        this.element()?.id ?? this.route.snapshot.params["id"] ?? null,
        entityExistsValidatorArgs,
      ),
    ]);
  }

  private setupForm() {
    this.rulesetsBuilderService.reset();

    const condition = this.isEditing()
      ? this.rulesetsBuilderService.getConditionForm(this.element()?.condition)
      : this.rulesetsBuilderService.buildDefaultConditionForm();

    const expectation = this.isEditing()
      ? this.getExpectationsForm(this.element()?.expectations)
      : this.rulesetsBuilderService.buildDefaultExpectationForm();

    this.formGroup = this.formBuilder.group({
      name: this.buildNameControl(),
      description: [this.element()?.description ?? ""],
      condition,
      expectation,
    });

    this.initialFormValue = this.formGroup.value;
    this.hasFormValuesChanged.set(false);

    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.hasFormValuesChanged.set(
          !this.formGroup.pristine && this.hasInitialFormValueChanged(this.formGroup.value),
        );
      }),
    );
  }

  public get expectationForms(): FormArray<FormGroup<IExpectationItemForm>> {
    return this.formGroup.get("expectation") as FormArray<FormGroup<IExpectationItemForm>>;
  }

  public onAddCriteria(formGroup: FormGroup) {
    const newConditionTypeForm = this.rulesetsBuilderService.buildDefaultConditionItemForm();

    (formGroup.get("itemForms") as FormArray).push(newConditionTypeForm);
  }

  private async getPayload(): Promise<IRuleset> {
    if (this.formGroup.invalid) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return null;
    }

    const { name, description, condition, expectation } = this.formGroup.value;

    let conditionTransformed: IConditionGroups;
    let expectations: IExpectation[];

    try {
      [conditionTransformed, expectations] = await Promise.all([
        this.getConditionPayload(condition),
        this.getExpectationsPayload(expectation),
      ]);
    } catch (error) {
      this.notificationService.showError(error);

      return null;
    }

    return {
      name,
      description: description || undefined,
      active: true,
      resourceType: this.resourceType,
      condition: conditionTransformed,
      expectations,
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
